import React, { useState, useRef, useEffect } from "react";
import useClickOutside from "../../hook/UseClickOutSide";
import { UserJson } from "../layout/Header";
import { Link, useNavigate } from "react-router-dom";
import { LogoutAPI } from "../../api/auth/Authentication";
import { ProfileFormType } from "../pages/profile/Profile";
import { getUserProfile } from "../../api/user/User";
import { useAuth } from "../../hook/use-auth";

interface UserProfileType {
  user?: UserJson;
}

function UserProfile({ user: initUser }: UserProfileType) {
  const [isOpenOption, setIsOpenOption] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const handleOpenOption = (value: boolean) => {
    setIsOpenOption(value);
  };

  const initFirst = useRef<boolean>(true);

  const { user, resetUser, initAuth } = useAuth();

  const handleNavigation = (path: string) => {
    if (path && path.trim() !== "") {
      navigate(`${path}`);
      setIsOpenOption(false);
    }
  };
  const userLogout = async () => {
    try {
      await LogoutAPI();
      resetUser();
      localStorage.removeItem("user");
      localStorage.removeItem("access_token");
      localStorage.removeItem("token_type");
      localStorage.removeItem("expired_at");
      localStorage.removeItem("refresh_token");
      navigate("/login");
    } catch (error: any) {
      console.log(error);
      if (error?.response.status === 401) {
        resetUser();
        localStorage.removeItem("user");
        localStorage.removeItem("access_token");
        localStorage.removeItem("token_type");
        localStorage.removeItem("expired_at");
        localStorage.removeItem("refresh_token");
        navigate("/login");
      }
    }
  };

  useClickOutside(dropdownRef, () => {
    setIsOpenOption(false);
  });

  const userProfile = async () => {
    try {
      const data: ProfileFormType = (await getUserProfile()) as ProfileFormType;
      if (data) {
        initAuth(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (initFirst.current) {
      userProfile();

      initFirst.current = false;
    }
  }, [initFirst.current, initUser]);

  return (
    <div className="relative" ref={dropdownRef}>
      <img
        onClick={() => handleOpenOption(!isOpenOption)}
        src={`${user?.avatar ? user?.avatar : "/images/avatar.svg"}`}
        alt="avatar"
        className="w-8 h-8 rounded-full object-fill hover:border-[1px] hover:border-primary-main cursor-pointer"
      />
      {isOpenOption && (
        <div className="absolute right-1 mt-1 w-auto duration-300 z-20">
          <div className="absolute right-1">
            <svg
              width="18"
              height="8"
              viewBox="0 0 22 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 9.25H20.6072L20.176 8.82248L12.767 1.47593C11.7909 0.508023 10.2091 0.508023 9.23298 1.47593L1.82397 8.82248L1.39281 9.25H2H20Z"
                fill="white"
                stroke="#E7E7E7"
                strokeWidth="0.5"
              />
            </svg>
          </div>
          <div className="absolute bg-background-paper -right-4 flex flex-col w-full min-w-[224px] pb-2 rounded-2xl border-[0.5px] border-gray-2 mt-2 shadow-[0px_12px_24px_-4px rgba(209, 209, 209, 0.16)]">
            <div className="p-4">
              <p className="font-medium text-base leading-6 text-text-primary whitespace-nowrap">
                {`${user?.firstName} ${user?.lastName}`}
              </p>
              <p className="font-normal text-sm text-text-secondary whitespace-nowrap">
                {user?.email}
              </p>
            </div>
            <div className="px-4">
              <hr className="border-[1px] border-gray-2" />
            </div>
            <div className="flex p-2 flex-col self-stretch gap-[1px]">
              <button
                onClick={() => handleNavigation("/dashboard")}
                className="py-2 pl-2 flex gap-2 self-stretch items-center rounded-xl hover:bg-gray-1 bg-opacity-70"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 10.9384C2.5 9.71422 3.06058 8.55744 4.02142 7.79888L9.52142 3.45677C10.9747 2.30948 13.0253 2.30948 14.4786 3.45677L19.9786 7.79888C20.9394 8.55744 21.5 9.71422 21.5 10.9384V17.5C21.5 19.7091 19.7091 21.5 17.5 21.5H16C15.4477 21.5 15 21.0523 15 20.5V17.5C15 16.3954 14.1046 15.5 13 15.5H11C9.89543 15.5 9 16.3954 9 17.5V20.5C9 21.0523 8.55228 21.5 8 21.5H6.5C4.29086 21.5 2.5 19.7091 2.5 17.5L2.5 10.9384Z"
                    stroke="#8E8E8E"
                    strokeWidth="1.5"
                  />
                </svg>
                <p className="text-sm font-normal text-text-primary leading-[22px]">
                  หน้าหลัก
                </p>
              </button>
              <button
                onClick={() => handleNavigation("/profile")}
                className="py-2 pl-2 flex gap-2 self-stretch items-center rounded-xl hover:bg-gray-1 bg-opacity-70"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="4"
                    cy="4"
                    r="4"
                    transform="matrix(-1 0 0 1 16 3)"
                    stroke="#8E8E8E"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M5 16.9347C5 16.0743 5.54085 15.3068 6.35109 15.0175V15.0175C10.004 13.7128 13.996 13.7128 17.6489 15.0175V15.0175C18.4591 15.3068 19 16.0743 19 16.9347V18.2502C19 19.4376 17.9483 20.3498 16.7728 20.1818L15.8184 20.0455C13.2856 19.6837 10.7144 19.6837 8.18162 20.0455L7.22721 20.1818C6.0517 20.3498 5 19.4376 5 18.2502V16.9347Z"
                    stroke="#8E8E8E"
                    strokeWidth="1.5"
                  />
                </svg>
                <p className="text-sm font-normal text-text-primary leading-[22px]">
                  ตั้งค่าโปรไฟล์
                </p>
              </button>
            </div>
            <div className="px-4">
              <hr className="border-[1px] border-gray-2" />
            </div>
            <div className="py-2 pl-4  items-center flex gap-2">
              <Link to={"/term-condition"}>
                <p className="text-[10px] font-normal leading-[18px text-text-primary] whitespace-nowrap">
                  Term & Condition
                </p>
              </Link>
              <p>•</p>
              <Link to={"/privacy-policy"}>
                <p className="text-[10px] font-normal leading-[18px text-text-primary] whitespace-nowrap">
                  Privacy policy
                </p>
              </Link>
            </div>
            <div className="px-4">
              <hr className="border-[1px] border-gray-2" />
            </div>
            <button
              onClick={userLogout}
              className="py-2 pl-4 flex gap-2 items-center"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 16V18C16 20.2091 14.2091 22 12 22H7C4.79086 22 3 20.2091 3 18V6C3 3.79086 4.79086 2 7 2H12C14.2091 2 16 3.79086 16 6V8"
                  stroke="#F05B50"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M19 15L21.2929 12.7071C21.6834 12.3166 21.6834 11.6834 21.2929 11.2929L19 9"
                  stroke="#F05B50"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M21 12L9 12"
                  stroke="#F05B50"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>

              <p className="text-error-main text-sm leading-[22px] font-normal">
                ออกจากระบบ
              </p>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
